import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { Voicemail } from 'lucide-react'
import { useNavigate } from 'react-router'
import { api } from '../../api/client.js'
import { getSocket } from '../../socket/client.js'

const POLL_INTERVAL_MS = 60_000
const VOICEMAIL_INBOX_ROUTE = '/agent/voicemail'

/** Shape of the GET /voicemails list response (Task 4). */
interface VoicemailListResponse {
  total: number
}

/**
 * UI-5b Task 12 — VoicemailBadge (spec §3).
 *
 * An agent-header icon button + count badge for the current agent's NEW
 * voicemails. Self-contained, mirroring `CallbacksWidget` (UI-3c):
 *
 *  - Polls `GET /voicemails?status=NEW&limit=1` on mount + every 60s and reads
 *    `total` from the response — NOT the array length, since `limit=1` caps the
 *    `voicemails` array at one entry.
 *  - Subscribes to the `voicemail:new` socket event and refetches when a new
 *    voicemail arrives, so the count updates in real time without waiting for
 *    the next poll.
 *  - Clicking navigates to the VoicemailInbox route (`/agent/voicemail`,
 *    wired by Task 14).
 *  - Renders `null` when the NEW count is 0 (per the plan).
 *
 * AgentHeaderBar gates this on the `voicemail:view_own` permission.
 */
export default function VoicemailBadge(): JSX.Element | null {
  const navigate = useNavigate()
  const [count, setCount] = useState(0)

  // Mounted guard: prevents setState calls after unmount.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const load = useCallback(async (): Promise<void> => {
    try {
      const resp = await api.get<VoicemailListResponse>(
        '/voicemails?status=NEW&limit=1',
      )
      if (mountedRef.current) {
        setCount(resp.total)
      }
    } catch {
      // Leave the count as-is on error; only log in dev.
      if (import.meta.env.DEV) {
        console.warn('[VoicemailBadge] fetch failed')
      }
    }
  }, [])

  // Initial fetch + 60-second poll (only when tab is active).
  useEffect(() => {
    void load()
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void load()
      }
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [load])

  // Real-time: refetch the authoritative `total` when a new voicemail arrives.
  useEffect(() => {
    const socket = getSocket()
    const handler = (): void => {
      void load()
    }
    socket.on('voicemail:new', handler)
    return () => {
      socket.off('voicemail:new', handler)
    }
  }, [load])

  if (count === 0) return null

  return (
    <button
      type="button"
      className="relative flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100"
      aria-label={`New voicemails, ${count}`}
      onClick={() => navigate(VOICEMAIL_INBOX_ROUTE)}
    >
      <Voicemail className="h-4 w-4" aria-hidden />
      <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#0f172a] px-1 text-[10px] font-bold text-white">
        {count}
      </span>
    </button>
  )
}
